/**
 * Der Kopplungscode in den Karten „Browser-Erweiterung“ und „Vespera
 * Desktop“ — EIN Baustein für beide. (29.09.2026, Ticket 2823)
 *
 * Martin, mit einem Bild der Mac-Karte: *„Und was mache ich mit dem
 * Peering-Code? Sollte erstens einmal der nicht so schön dargestellt
 * werden, so wie es bei der Extension von Chrome ist.“* Die Mac-Karte baute
 * ihren Code selbst (`.connector-code-big`, dafür gab es gar kein CSS) —
 * der Code stand als schlichter Text da.
 *
 * Jetzt baut beide Karten dieselbe Stelle: groß und gesperrt, ein
 * Kopieren-Knopf, und die Restzeit läuft sichtbar mit. Aussehen: style.css
 * `.ext-code-*` (keine Inline-Styles).
 *
 * ⚠️ Die Restzeit tickt jede Sekunde, aber nur, solange der Code zu sehen
 *    ist: verschwindet die Box (versteckt, Ansicht verlassen) oder ist der
 *    Code abgelaufen, hält der Takt von selbst an.
 */

/** So lange gilt ein Code am Server (/api/ext/pair-code, /api/daemon/pair-code). */
export const CODE_GUELTIG_MS = 5 * 60_000;

/** „noch 4:59 gültig“ — oder, wenn die Zeit um ist, was jetzt zu tun ist. */
export function restzeitText(restMs: number): string {
  if (restMs <= 0) return "Abgelaufen – bitte einen neuen Code erzeugen.";
  const s = Math.ceil(restMs / 1000);
  return `noch ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} gültig`;
}

/**
 * Bis wann der Code gilt, in ms nach der Uhr DIESES Geräts.
 *
 * ⚠️ Der Server nennt die Zeit nach seiner Uhr (Millisekunden oder ISO-Text).
 *    Geht die Uhr des Geräts falsch, stünde sonst gleich „abgelaufen“ da
 *    oder eine Stunde Restzeit. Liegt die Angabe außerhalb dessen, was ein
 *    frischer Code haben kann, gilt die bekannte Dauer ab jetzt.
 */
export function gueltigBisMs(angabe: number | string | null | undefined, jetzt = Date.now()): number {
  const ms = typeof angabe === "number" ? angabe : typeof angabe === "string" ? Date.parse(angabe) : NaN;
  const rest = ms - jetzt;
  if (!Number.isFinite(rest) || rest <= 0 || rest > CODE_GUELTIG_MS + 60_000) return jetzt + CODE_GUELTIG_MS;
  return ms;
}

interface KopplungsCode {
  /** Die Box — versteckt, bis ein Code da ist. */
  box: HTMLElement;
  /** Einen Code zeigen; `hinweis` sagt, wo er einzugeben ist. */
  zeige(code: string, gueltigBis: number | string | null, hinweis: string): void;
  verstecke(): void;
  /** Nur den Satz darunter tauschen — der Code und seine Zeit bleiben. */
  hinweis(text: string): void;
  /** Der gezeigte Code, solange er noch mindestens `reserveMs` gilt — sonst null. */
  gueltigerCode(reserveMs?: number): string | null;
}

const SVG = "http://www.w3.org/2000/svg";

/** Kopieren-Zeichen bzw. Haken, sobald kopiert ist. */
function kopierZeichen(knopf: HTMLButtonElement, art: "copy" | "done"): void {
  while (knopf.firstChild) knopf.removeChild(knopf.firstChild);
  const svg = document.createElementNS(SVG, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("aria-hidden", "true");
  const pfad = document.createElementNS(SVG, "path");
  pfad.setAttribute("fill", "currentColor");
  pfad.setAttribute(
    "d",
    art === "done"
      ? "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
      : "M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z",
  );
  svg.appendChild(pfad);
  knopf.appendChild(svg);
}

/** Die Box bauen. `label` steht klein darüber („Pairing-Code“, „Kopplungscode“). */
export function baueKopplungsCode(label: string): KopplungsCode {
  const box = document.createElement("div");
  box.className = "ext-code-box";
  box.hidden = true;

  const titel = document.createElement("span");
  titel.className = "ext-code-label";
  titel.textContent = label;
  box.appendChild(titel);

  const zeile = document.createElement("div");
  zeile.className = "ext-code-row";
  const wert = document.createElement("strong");
  wert.className = "ext-code-value";
  zeile.appendChild(wert);

  const kopieren = document.createElement("button");
  kopieren.type = "button";
  kopieren.className = "ext-code-copy";
  kopieren.setAttribute("aria-label", "Code kopieren");
  kopieren.title = "Code kopieren";
  kopierZeichen(kopieren, "copy");
  const kopiere = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(wert.textContent ?? "");
      kopieren.dataset.state = "done";
      kopierZeichen(kopieren, "done");
      setTimeout(() => {
        delete kopieren.dataset.state;
        kopierZeichen(kopieren, "copy");
      }, 1400);
    } catch {
      /* Zwischenablage nicht verfügbar — der Code steht ja da. */
    }
  };
  kopieren.addEventListener("click", () => void kopiere());
  zeile.appendChild(kopieren);
  box.appendChild(zeile);

  const rest = document.createElement("span");
  rest.className = "ext-code-rest";
  box.appendChild(rest);

  const hinweisEl = document.createElement("span");
  hinweisEl.className = "ext-code-hint";
  box.appendChild(hinweisEl);

  let bis = 0;
  let takt: number | null = null;
  const halt = (): void => {
    if (takt !== null) window.clearInterval(takt);
    takt = null;
  };
  const tick = (): void => {
    const restMs = bis - Date.now();
    rest.textContent = restzeitText(restMs);
    const abgelaufen = restMs <= 0;
    box.toggleAttribute("data-abgelaufen", abgelaufen);
    kopieren.disabled = abgelaufen;
    if (abgelaufen || box.hidden || !box.isConnected) halt();
  };

  return {
    box,
    zeige(code, gueltigBis, hinweis) {
      wert.textContent = code;
      hinweisEl.textContent = hinweis;
      bis = gueltigBisMs(gueltigBis);
      box.hidden = false;
      halt();
      tick();
      takt = window.setInterval(tick, 1000);
    },
    verstecke() {
      box.hidden = true;
      halt();
    },
    hinweis(text) {
      hinweisEl.textContent = text;
    },
    gueltigerCode(reserveMs = 0) {
      if (box.hidden || !wert.textContent) return null;
      return bis - Date.now() > reserveMs ? wert.textContent : null;
    },
  };
}
